import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PatientModel } from '../../../models/patient';
import { PatientInvoiceModel } from '../../../models/client';
import {
  AccountService,
  PatientsService,
  PatientInvoiceService,
  PatientResponsibilityAccountsService,
  AlertService,
  ClientMessagingService,
  ParentOrientationVideosService,
  AppConfig
} from '../../../core';
import { first, switchMap, tap } from 'rxjs/operators';
import { Router, ParamMap, ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import * as moment from 'moment';
import { GeneralSettingsModel } from '../../../models/account/generalSettingsModel';
import { PaymentPlanScheduleViewModel } from '../../../models/PatientResponsibilityAccount';
import { MessageThreadModel } from '../../../models/messaging/';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormBuilderService } from '../../../core/services/formBuilder.service';
import { FBFormAssignmentModel } from '../../../models/formBuilder/FormBuilderModels';
import { ParentOrientationPlaylist } from '../../../models/parentOrientationPlaylist';
import { ParentOrientationVideoModel } from '../../../models/parentOrientationVideoModel';


@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {

  @ViewChild('orientationVideoPlayer') orientationVideoPlayer?: ElementRef<HTMLVideoElement>;

  public canSendReceiveMessage: boolean = false;
  public showAll: boolean = false;
  public showNotice = false;
  public loadingPatients = false;
  public loadingInvoices = false;
  public loadingMessages = false;
  creditCardPaymentEnabled: boolean = false;
  public unreadThreads: MessageThreadModel[];
  public paymentSchedule: PaymentPlanScheduleViewModel[] | null = null;
  public parentOrientationPlaylist = new ParentOrientationPlaylist();
  public selectedOrientationVideoURL: SafeResourceUrl | null = null;
  public orientationVideoPlaybackError: string | null = null;
  private orientationVideoRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private orientationVideoRefreshInProgress = false;
  private orientationVideoRecoveryAttemptedForID: number | null = null;
  
  get generalSettings(): GeneralSettingsModel {
    return this.accountService ? this.accountService.generalSettings : null;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private accountService: AccountService,
    private patientsService: PatientsService,
    private patientResponsibilityAccountsService: PatientResponsibilityAccountsService,
    public patientInvoiceService: PatientInvoiceService,
    private clientMessagingService: ClientMessagingService,
    private formBuilderService: FormBuilderService,
    private parentOrientationVideosService: ParentOrientationVideosService,
    private alertService: AlertService,
  ) {
    this.loadingPatients = true;
    patientsService.$patients.subscribe(data => {

      //this.checkNotice();

    });

    this.clientMessagingService.$unreadMessages.subscribe(messages => {
      this.canSendReceiveMessage = this.clientMessagingService.canSendReceiveMessage;
      this.unreadThreads = messages
    });
    this.clientMessagingService.$receivers.subscribe(data => {
      this.canSendReceiveMessage = this.clientMessagingService.canSendReceiveMessage;
    });
  }
  
  get loggedUserfirstName() {
    return this.accountService.currentUser.firstName;
  }
  get currentPatientID() {
    return this.patientsService.currentPatientID;
  }
  get currentInquiryID() {
    return this.patientsService.currentInquiryID;
  }

  loadUnreadMessages() {
    this.loadingMessages = true;
    this.clientMessagingService.$unreadMessages.subscribe(data => {
      this.unreadThreads = data;
      this.loadingMessages = false;
    });
  }

  hasActionRequired(patient): boolean {
    return (!this.formsAndDocsSigned(patient) || patient.insurancesReviewPending);
  }

  getPatientDisplayName(patient: PatientModel): string {
    if (!patient) {
      return '';
    }

    const fullName = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
    return patient.inactive ? `${fullName} (INACTIVE)` : fullName;
  }

  formsAndDocsSigned(patient) {
    return patient != null
      && !(patient.requiredFormsNotSigned > 0
        || (!patient.hasAllRequiredDocsSigned
          && patient.documentsNotSigned > 0));
  }

  get openPatientInvoices() {
    let vls = [];
    if (this.patientInvoiceService.PatientInvoices != null) {
      for (let i = 0; i < this.patientInvoiceService.PatientInvoices.length; i++) {
        if (this.patientInvoiceService.PatientInvoices[i].amountDue > 0)
          vls.push(this.patientInvoiceService.PatientInvoices[i]);
      }
    }

    return vls;
  }
  
  get totalAmountDue() {
    let vl = 0;
    if (this.patientInvoiceService.PatientInvoices != null) {
      for (let i = 0; i < this.patientInvoiceService.PatientInvoices.length; i++) {
        vl += this.patientInvoiceService.PatientInvoices[i].amountDue;

      }
    }

    return vl;
  }
  get totalScheduledAmount() {
    let vl = 0;
    if (this.paymentSchedule != null) {
      for (let i = 0; i < this.paymentSchedule.length; i++) {
        vl += this.paymentSchedule[i].dueAmount;
      }
    }

    return vl;
  }

  

  get patients() {
    return this.patientsService.patients;
  }
  get canMakePayment() {
    return this.creditCardPaymentEnabled && this.patientResponsibilityAccountsService.accounts &&  this.patientResponsibilityAccountsService.accounts.find(a => a.canMakePayments) != null;
  }

  get parentOrientationVideos(): ParentOrientationVideoModel[] {
    return this.parentOrientationPlaylist.videos;
  }

  get selectedOrientationVideo(): ParentOrientationVideoModel | null {
    return this.parentOrientationPlaylist.selectedVideo;
  }

  selectOrientationVideo(video: ParentOrientationVideoModel): void {
    this.parentOrientationPlaylist.select(video);
    this.resetOrientationVideoPlaybackState();
    this.updateSelectedOrientationVideoURL();
    this.scheduleSelectedOrientationVideoRefresh();
  }

  playNextOrientationVideo(): void {
    this.parentOrientationPlaylist.selectNext();
    this.resetOrientationVideoPlaybackState();
    this.updateSelectedOrientationVideoURL();
    this.scheduleSelectedOrientationVideoRefresh();
  }

  handleOrientationVideoPlaybackError(): void {
    const selectedVideo = this.selectedOrientationVideo;
    if (!selectedVideo) {
      return;
    }

    if (!selectedVideo.isUploaded) {
      this.showOrientationVideoPlaybackError('This video could not be played. Select it again to retry.');
      return;
    }

    if (this.orientationVideoRefreshInProgress) {
      return;
    }

    if (this.orientationVideoRecoveryAttemptedForID === selectedVideo.id) {
      this.showOrientationVideoPlaybackError(
        'This video could not be played after refreshing it. Select it again to retry.'
      );
      return;
    }

    if (this.orientationVideoRefreshTimer) {
      clearTimeout(this.orientationVideoRefreshTimer);
      this.orientationVideoRefreshTimer = null;
    }
    this.orientationVideoRecoveryAttemptedForID = selectedVideo.id;
    this.orientationVideoPlaybackError = null;
    this.refreshSelectedOrientationVideo(true);
  }

  isPastDue(invoiceDueDate, invoiceStatusID) {
    // Don't show as past due if invoice is in payment plan
    if (invoiceStatusID === 9)
      return false;
    return moment(invoiceDueDate).isBefore(moment().subtract(1, 'days').endOf('day'));
  }

  isInPaymentPlan(invoiceStatusID) {
    return invoiceStatusID === 9;
  }

  ngOnInit() {
    if (this.patients == null) {
      this.showAll = false;
      //  this._client.getAll().subscribe((vl) => { });
    } else {
      this.showAll = true;

      // Notice
      //this.checkNotice();

    }

    this.creditCardPaymentEnabled = this.accountService
      && this.accountService.currentUser
      && this.accountService.currentUser.systemFeatureIDs
      && !!this.accountService.currentUser.systemFeatureIDs.find(x => x === 82);



    this.route.queryParams.subscribe(params => {
      this.showAll = (params['show'] == "all") || (this.patients != null);
      // console.log('Show All: ' + this.showAll + ' Clients: ' + (this.clients != null ? this.clients.length : -1));
    });

    this.clientMessagingService.getMessageThreads().subscribe(messages => { });

    this.parentOrientationVideosService.getVideos().subscribe({
      next: videos => {
        this.parentOrientationPlaylist.setVideos(videos);
        this.resetOrientationVideoPlaybackState();
        this.updateSelectedOrientationVideoURL();
        this.scheduleSelectedOrientationVideoRefresh();
      },
      error: () => {
        this.parentOrientationPlaylist.setVideos([]);
        this.resetOrientationVideoPlaybackState();
        this.updateSelectedOrientationVideoURL();
        this.scheduleSelectedOrientationVideoRefresh();
      }
    });



    this.patientResponsibilityAccountsService.getPaymentSchedule().subscribe(items => {
      this.paymentSchedule = items;
    });
  }

  private updateSelectedOrientationVideoURL(): void {
    this.selectedOrientationVideoURL = this.selectedOrientationVideo?.useIframe
      ? this.sanitizer.bypassSecurityTrustResourceUrl(this.selectedOrientationVideo.playbackURL)
      : null;
  }

  private resetOrientationVideoPlaybackState(): void {
    this.orientationVideoPlaybackError = null;
    this.orientationVideoRecoveryAttemptedForID = null;
  }

  private showOrientationVideoPlaybackError(message: string): void {
    if (this.orientationVideoRefreshTimer) {
      clearTimeout(this.orientationVideoRefreshTimer);
      this.orientationVideoRefreshTimer = null;
    }
    this.orientationVideoPlaybackError = message;
  }

  private scheduleSelectedOrientationVideoRefresh(): void {
    if (this.orientationVideoRefreshTimer) {
      clearTimeout(this.orientationVideoRefreshTimer);
      this.orientationVideoRefreshTimer = null;
    }

    const video = this.selectedOrientationVideo;
    if (!video?.isUploaded || !video.playbackURLExpiresUtc) {
      return;
    }

    const expiresAt = new Date(video.playbackURLExpiresUtc).getTime();
    const delay = this.parentOrientationPlaylist.needsPlaybackRefresh(video)
      ? 0
      : Number.isFinite(expiresAt)
      ? Math.max(0, expiresAt - Date.now() - 5 * 60 * 1000)
      : 0;
    this.orientationVideoRefreshTimer = setTimeout(
      () => this.refreshSelectedOrientationVideo(),
      delay
    );
  }

  refreshSelectedOrientationVideo(isPlaybackRecovery = false): void {
    const selectedVideo = this.selectedOrientationVideo;
    if (!selectedVideo?.isUploaded || this.orientationVideoRefreshInProgress) {
      return;
    }

    const submittedVideoID = selectedVideo.id;
    const currentPlayer = this.orientationVideoPlayer?.nativeElement;
    const playbackPosition = currentPlayer?.currentTime || 0;
    const resumePlayback = currentPlayer ? !currentPlayer.paused && !currentPlayer.ended : false;
    this.orientationVideoRefreshInProgress = true;

    this.parentOrientationVideosService.getPlayback(submittedVideoID).subscribe({
      next: refreshedVideo => {
        if (this.selectedOrientationVideo?.id !== submittedVideoID) {
          return;
        }

        if (currentPlayer && playbackPosition > 0) {
          currentPlayer.addEventListener('loadedmetadata', () => {
            currentPlayer.currentTime = Math.min(
              playbackPosition,
              Number.isFinite(currentPlayer.duration) ? currentPlayer.duration : playbackPosition
            );
            if (resumePlayback) {
              currentPlayer.play().catch(() => { });
            }
          }, { once: true });
        }

        this.parentOrientationPlaylist.replace(refreshedVideo);
        if (!isPlaybackRecovery) {
          this.orientationVideoRecoveryAttemptedForID = null;
        }
        this.updateSelectedOrientationVideoURL();
        setTimeout(() => currentPlayer?.load());
        this.scheduleSelectedOrientationVideoRefresh();
      },
      error: () => {
        const message = 'This video could not be refreshed. Select it again to retry.';
        if (isPlaybackRecovery) {
          this.showOrientationVideoPlaybackError(message);
        }
        this.alertService.error(message);
      }
    }).add(() => {
      this.orientationVideoRefreshInProgress = false;
    });
  }

  ngOnDestroy(): void {
    if (this.orientationVideoRefreshTimer) {
      clearTimeout(this.orientationVideoRefreshTimer);
    }
  }

  //checkNotice() {
  //  const endDate = new Date('2022-10-15');
  //  const today = new Date();
  //  if (today < endDate) {
  //    this.showNotice = this.patients.some(patient => patient.hasMedClinicAuthorization || patient.hasPsychMedAuthorization);
  //  }
  //}

  signFile(form: FBFormAssignmentModel) {
    this.router.navigate([`/patient/${form.inquiryID}/forms/sign/${form.formSubmissionID}`], { queryParams: { formName: form.form.name } });
  }
  
  get unreadThreadsCount() {
    return this.unreadThreads.length;
  }

  goToThread(thread: MessageThreadModel) {
    this.router.navigate(['/messaging/message-view/'+  thread.messageThreadID]);
  }

  get urlForPictureRequest() {
    return `${AppConfig.settings.apiServer.webApiURL}/Messages/SenderPhoto/`;
  }

  getPhoto(photoID?: number) {
    return `${AppConfig.settings.apiServer.webApiURL}/Messages/SenderPhoto/${photoID}`;
  }



  getPaymentPlanFrequency(item: PaymentPlanScheduleViewModel) {
    var r = item.paymentPlanFrequency;

    if ((item.paymentPlanFrequencyID == 2)
      || (item.paymentPlanFrequencyID == 3))
      r += " on " + this.getDayOfTheWeek(item.paymentDay);
    else if (item.paymentPlanFrequencyID == 4)
      r = "First " + this.getDayOfTheWeek(item.paymentDay) + " of the month";
    else if (item.paymentPlanFrequencyID == 6)
      r = "Last " + this.getDayOfTheWeek(item.paymentDay) + " of the month";

    else if (item.paymentPlanFrequencyID == 5)
      r = "Every " + this.getDayOfTheMonth(item.paymentDay) + " day of the month";
    else if ((item.paymentPlanFrequencyID == 7)
      || (item.paymentPlanFrequencyID == 8)
      || (item.paymentPlanFrequencyID == 9))
      r = r + " on " + this.getDayOfTheMonth(item.paymentDay) + " day of the month";

    return r;
  }

  getDayOfTheWeek(dayOfTheWeek: Number) {
    switch (dayOfTheWeek) {
      case 0:
        return "Sunday";
        break;
      case 1:
        return "Monday";
        break;
      case 2:
        return "Tuesday";
        break;
      case 3:
        return "Wednesday";
        break;
      case 4:
        return "Thursday";
        break;
      case 5:
        return "Friday";
        break;
      case 6:
        return "Saturday";
    }
  }

  getDayOfTheMonth(dayOfTheMonth: Number) {
    if (dayOfTheMonth == 1)
      return "1st";
    if (dayOfTheMonth == 2)
      return "2nd";
    return dayOfTheMonth + "th";
  }
}
